import { Slot } from 'radix-ui';
import type * as React from 'react';
import { cn } from '@/lib/utils';
import { Icon } from '../icons/Icon';

// shadcn/ui (AD #39): shadcn's Breadcrumb, adapted to the CRM's rules — the separator glyph comes from the
// icon registry (the one module that imports lucide-react), the links keep the global :focus-visible ring,
// and the current page is a plain `aria-current="page"` span: shadcn gives it `role="link"` with
// `aria-disabled`, which would announce a link that goes nowhere. The ellipsis part is left out until a
// trail needs collapsing.

function Breadcrumb({ ...props }: React.ComponentProps<'nav'>) {
    return <nav aria-label="Breadcrumb" data-slot="breadcrumb" {...props} />;
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<'ol'>) {
    return (
        <ol
            data-slot="breadcrumb-list"
            className={cn('flex flex-wrap items-center gap-1.5 break-words text-muted-foreground', className)}
            {...props}
        />
    );
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<'li'>) {
    return (
        <li
            data-slot="breadcrumb-item"
            className={cn('inline-flex min-w-0 items-center gap-1.5', className)}
            {...props}
        />
    );
}

function BreadcrumbLink({
    asChild,
    className,
    ...props
}: React.ComponentProps<'a'> & {
    asChild?: boolean;
}) {
    const Comp = asChild ? Slot.Root : 'a';

    return (
        <Comp
            /* testid-exempt: a shadcn primitive; the caller passes data-testid through props */
            data-slot="breadcrumb-link"
            className={cn('transition-colors hover:text-foreground', className)}
            {...props}
        />
    );
}

function BreadcrumbPage({ className, ...props }: React.ComponentProps<'span'>) {
    return (
        <span
            data-slot="breadcrumb-page"
            aria-current="page"
            className={cn('font-normal text-foreground', className)}
            {...props}
        />
    );
}

function BreadcrumbSeparator({ children, className, ...props }: React.ComponentProps<'li'>) {
    return (
        <li
            data-slot="breadcrumb-separator"
            role="presentation"
            aria-hidden="true"
            className={cn('inline-flex', className)}
            {...props}
        >
            {children ?? <Icon name="breadcrumbSeparator" size="sm" />}
        </li>
    );
}

export { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator };
